/**
 * Swing Trade Strategy — faithful port of MT4 MT4_Swing_Trade_V2.mq4
 *
 * Uses DUAL SuperTrend system (Swing.mq4 algorithm run twice):
 *   - ST1 & ST2 with configurable period/multiplier per preset
 *   - Signal fires when BOTH SuperTrends agree on direction
 *   - Goes NEUTRAL when STs disagree (ST_SIGNAL_ON_BOTH_MATCHING = true)
 *
 * Presets:
 *   Standard   → ST1(11, 4.0) + ST2(10, 2.5)
 *   Long Trend → ST1(10, 3.0) + ST2(10, 3.0)
 *   NSE        → ST1(10, 3.0) + ST2(10, 3.0)
 *
 * SL/TP (Candle-range based from MT4):
 *   range = signal candle High − Low
 *   SL  = Entry − direction × 3.5 × range
 *   TP1 = Entry + direction × 3.5 × range
 *   TP2 = Entry + direction × 7.0 × range
 *
 * Trailing Stop: Percentage-based (0.35%)
 *
 * MT4 features ported:
 *   - Dual SuperTrend confirmation with NEUTRAL on disagreement
 *   - Buy/Sell arrow markers
 *   - Custom colored candles: Green (#379244), DarkRed (#B40000), Black (#000000)
 *   - Candle-range SL/TP multipliers
 *   - Percentage-based trailing stop
 *   - Target/SL hit detection with markers
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

const SL_MULT = 3.5;
const TP1_MULT = 3.5;
const TP2_MULT = 7.0;
const TSL_PERCENT = 0.35;

// ─── Presets ─────────────────────────────────────────────────────
interface Preset {
  st1Period: number; st1Mult: number;
  st2Period: number; st2Mult: number;
}
const PRESETS: Record<string, Preset> = {
  standard:   { st1Period: 11, st1Mult: 4.0, st2Period: 10, st2Mult: 2.5 },
  long_trend: { st1Period: 10, st1Mult: 3.0, st2Period: 10, st2Mult: 3.0 },
  nse:        { st1Period:  8, st1Mult: 2.0, st2Period: 16, st2Mult: 3.5 },
};

// ─── ATR computation (Wilder smoothing) ──────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── SuperTrend computation (Swing.mq4 algorithm) ────────────────
function computeSuperTrend(
  candles: CandleData[], period: number, multiplier: number,
): { direction: Direction[] } {
  const len = candles.length;
  const direction: Direction[] = new Array(len).fill(DIR_LONG);
  if (len < period + 2) return { direction };

  const atr = computeATR(candles, period);
  const upperRaw: number[] = new Array(len).fill(0);
  const lowerRaw: number[] = new Array(len).fill(0);
  const upper: number[] = new Array(len).fill(0);
  const lower: number[] = new Array(len).fill(0);

  for (let i = 0; i < len; i++) {
    const mid = (candles[i].high + candles[i].low) / 2.0;
    upperRaw[i] = mid + multiplier * atr[i];
    lowerRaw[i] = mid - multiplier * atr[i];
  }
  upper[0] = upperRaw[0]; lower[0] = lowerRaw[0];

  for (let i = 1; i < len; i++) {
    const close = candles[i].close;
    if (close > upper[i - 1]) direction[i] = DIR_LONG;
    else if (close < lower[i - 1]) direction[i] = DIR_SHORT;
    else direction[i] = direction[i - 1];

    lower[i] = lowerRaw[i];
    if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) lower[i] = lower[i - 1];
    upper[i] = upperRaw[i];
    if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) upper[i] = upper[i - 1];

    const prevDir = direction[i - 1];
    if (direction[i] === DIR_SHORT && prevDir === DIR_LONG) upper[i] = upperRaw[i];
    if (direction[i] === DIR_LONG && prevDir === DIR_SHORT) lower[i] = lowerRaw[i];
  }
  return { direction };
}

// ─── Dashboard interface ────────────────────────────────────────
export interface SwingTradeDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  preset: string;
  entry: number | null;
  stopLoss: number | null;
  trailingStop: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  candleState: "long" | "short" | "neutral";
}

export const swingTradeIndicator: IndicatorDefinition = {
  id: "swing_trade",
  name: "Swing Trade",
  shortName: "SWT",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "preset", label: "Preset", type: "select", default: "standard", options: [
      { label: "Standard", value: "standard" },
      { label: "Long Trend", value: "long_trend" },
      { label: "NSE Segment", value: "nse" },
    ]},
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showTrailingStop", label: "Show Trailing SL", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [{ label: "Yes", value: "yes" }, { label: "No", value: "no" }] },
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#1565C0", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#B40000", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#379244", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#379244", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const presetKey = params.preset ?? "standard";
    const displayLevels = params.displayLevels ?? "yes";
    const showBackground = params.showBackground ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";
    const preset = PRESETS[presetKey] || PRESETS.standard;
    const len = candles.length;
    const minBars = Math.max(preset.st1Period, preset.st2Period) + 3;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] },
      { key: "target1", data: [] }, { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    // ════════════════════════════════════════════════════════════
    // 1. Dual SuperTrend
    // ════════════════════════════════════════════════════════════
    const st1 = computeSuperTrend(candles, preset.st1Period, preset.st1Mult);
    const st2 = computeSuperTrend(candles, preset.st2Period, preset.st2Mult);

    // Combined direction: NEUTRAL when STs disagree
    const combinedDir: Direction[] = new Array(len).fill(DIR_NONE);
    for (let i = 0; i < len; i++) {
      if (st1.direction[i] === DIR_LONG && st2.direction[i] === DIR_LONG) {
        combinedDir[i] = DIR_LONG;
      } else if (st1.direction[i] === DIR_SHORT && st2.direction[i] === DIR_SHORT) {
        combinedDir[i] = DIR_SHORT;
      } else {
        combinedDir[i] = DIR_NONE; // NEUTRAL — key difference from Max Wealth
      }
    }

    // ════════════════════════════════════════════════════════════
    // 2. Signal Generation
    // ════════════════════════════════════════════════════════════
    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number }[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let lastSignalDir: Direction = DIR_NONE;
    let lastSignalBar = -1;
    let currentSignal: Direction = DIR_NONE;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const curCombined = combinedDir[i];

      let newSignal = false;
      if (curCombined === DIR_LONG && currentSignal !== DIR_LONG) {
        newSignal = true;
        currentSignal = DIR_LONG;
      } else if (curCombined === DIR_SHORT && currentSignal !== DIR_SHORT) {
        newSignal = true;
        currentSignal = DIR_SHORT;
      } else if (curCombined === DIR_NONE) {
        // STs disagree → go NEUTRAL (ST_SIGNAL_ON_BOTH_MATCHING = true)
        currentSignal = DIR_NONE;
      }

      if (newSignal && curCombined !== DIR_NONE) {
        const entry = candles[i].close;
        const eHigh = candles[i].high;
        const eLow = candles[i].low;
        const candleRange = eHigh - eLow;
        const dir = curCombined === DIR_LONG ? 1.0 : -1.0;

        SIGNAL[i] = curCombined;
        ENTRY[i] = roundToTick(entry, tick);
        SL[i] = roundToTick(entry - dir * SL_MULT * candleRange, tick);
        TP1[i] = roundToTick(entry + dir * TP1_MULT * candleRange, tick);
        TP2[i] = roundToTick(entry + dir * TP2_MULT * candleRange, tick);
        TSL[i] = SL[i];

        lastSignalDir = curCombined;
        lastSignalBar = i;

        if (curCombined === DIR_LONG) {
          markers.push({ time: candles[i].time, position: "belowBar", color: "#379244", shape: "arrowUp", text: "", size: 2 });
          arrowMarkers.push({ time: candles[i].time, direction: "long" as const, price: candles[i].low });
        } else {
          markers.push({ time: candles[i].time, position: "aboveBar", color: "#B40000", shape: "arrowDown", text: "", size: 2 });
          arrowMarkers.push({ time: candles[i].time, direction: "short" as const, price: candles[i].high });
        }
      } else {
        SIGNAL[i] = lastSignalDir;
        ENTRY[i] = ENTRY[i - 1] || 0;
        SL[i] = SL[i - 1] || 0;
        TP1[i] = TP1[i - 1] || 0;
        TP2[i] = TP2[i - 1] || 0;

        // Percentage-based trailing stop (0.35%) — runs only on non-signal bars.
        // On the signal bar TSL is already set to SL (the initial hard-stop distance).
        if (lastSignalDir === DIR_LONG && lastSignalBar >= 0) {
          const pctTSL = roundToTick(candles[i].close * (100.0 - TSL_PERCENT) / 100.0, tick);
          TSL[i] = Math.max(TSL[i - 1] || SL[lastSignalBar], pctTSL);
        } else if (lastSignalDir === DIR_SHORT && lastSignalBar >= 0) {
          const pctTSL = roundToTick(candles[i].close * (100.0 + TSL_PERCENT) / 100.0, tick);
          TSL[i] = Math.min(TSL[i - 1] || SL[lastSignalBar], pctTSL);
        } else {
          TSL[i] = TSL[i - 1] || 0;
        }
      }

      // Candle colors — NEUTRAL when STs disagree
      if (curCombined === DIR_NONE) {
        candleColorState[i] = "neutral";
      } else if (lastSignalDir === DIR_LONG) {
        candleColorState[i] = "long";
      } else if (lastSignalDir === DIR_SHORT) {
        candleColorState[i] = "short";
      } else {
        candleColorState[i] = "neutral";
      }
    }

    // Forward-fill forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      const liveIdx = len - 1;
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar];
        ENTRY[i] = ENTRY[lastClosedBar]; SL[i] = SL[lastClosedBar]; TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar]; TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
      // Update live bar TSL from current tick price — ensures the T-SL line
      // moves instantly with every price update, not just on bar close.
      if (lastSignalDir !== DIR_NONE && lastSignalBar >= 0) {
        const liveClose = candles[liveIdx].close;
        if (lastSignalDir === DIR_LONG) {
          const pctTSL = roundToTick(liveClose * (100.0 - TSL_PERCENT) / 100.0, tick);
          TSL[liveIdx] = Math.max(TSL[lastClosedBar], pctTSL);
        } else {
          const pctTSL = roundToTick(liveClose * (100.0 + TSL_PERCENT) / 100.0, tick);
          TSL[liveIdx] = Math.min(TSL[lastClosedBar], pctTSL);
        }
      }
    }

    // ════════════════════════════════════════════════════════════
    // 3. Target / SL hit detection — chronological bar-by-bar
    //    Priority: targets hit BEFORE stops → book target profit
    // ════════════════════════════════════════════════════════════
    let status: SwingTradeDashboard["status"] = "Active";
    let tp1Hit = false, tp2Hit = false, slHit = false, tslHit = false;
    let bookedPL = 0;

    if (lastSignalBar >= 0) {
      const ev = ENTRY[lastSignalBar], sv = SL[lastSignalBar];
      const t1v = TP1[lastSignalBar], t2v = TP2[lastSignalBar];
      const sigDir = SIGNAL[lastSignalBar];

      // Find the FIRST bar each level is breached
      let tp1HitBar = -1, tp2HitBar = -1, slHitBar = -1, tslHitBar = -1;
      let tslHitPrice = 0;

      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar + 1; i < len; i++) {
        if (sigDir === DIR_LONG) {
          if (tp1HitBar < 0 && candles[i].high >= t1v) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].high >= t2v) tp2HitBar = i;
          if (slHitBar < 0 && candles[i].low <= sv) slHitBar = i;
          if (tslHitBar < 0 && candles[i].low <= TSL[i] && TSL[i] > sv) { tslHitBar = i; tslHitPrice = TSL[i]; }
        } else {
          if (tp1HitBar < 0 && candles[i].low <= t1v) tp1HitBar = i;
          if (tp2HitBar < 0 && candles[i].low <= t2v) tp2HitBar = i;
          if (slHitBar < 0 && candles[i].high >= sv) slHitBar = i;
          if (tslHitBar < 0 && candles[i].high >= TSL[i] && TSL[i] < sv) { tslHitBar = i; tslHitPrice = TSL[i]; }
        }
      }

      tp1Hit = tp1HitBar >= 0;
      tp2Hit = tp2HitBar >= 0;
      slHit = slHitBar >= 0;
      tslHit = tslHitBar >= 0;

      const earliestStopBar = slHit && tslHit
        ? Math.min(slHitBar, tslHitBar)
        : slHit ? slHitBar : tslHit ? tslHitBar : Infinity;

      const plSign = sigDir === DIR_LONG ? 1 : -1;

      if (tp2Hit && tp2HitBar <= earliestStopBar) {
        status = "Target2 Hit"; tp1Hit = true; tp2Hit = true;
        bookedPL = plSign * (t2v - ev);
      } else if (tp1Hit && tp1HitBar <= earliestStopBar) {
        status = "Target1 Hit";
        bookedPL = plSign * (t1v - ev);
      } else if (slHit && (!tslHit || slHitBar <= tslHitBar)) {
        status = "Stop Loss Hit";
        bookedPL = plSign * (sv - ev);
      } else if (tslHit) {
        status = "Trailing SL Hit";
        bookedPL = plSign * (tslHitPrice - ev);
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata
    // ════════════════════════════════════════════════════════════
    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const cs = SIGNAL[uiBar], ce = ENTRY[uiBar];

    let dashSignal: SwingTradeDashboard["signal"] = null;
    let dashTrend: SwingTradeDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (cs !== DIR_NONE && ce > 0) {
      if (cs === DIR_LONG) { dashSignal = "BUY"; dashTrend = "LONG"; livePL = currentPrice - ce; }
      else { dashSignal = "SELL"; dashTrend = "SHORT"; livePL = ce - currentPrice; }
    }
    // Once any level is hit the trade is closed — live P&L becomes 0
    if (status !== "Active") livePL = 0;

    const presetLabels: Record<string, string> = {
      standard: "Standard", long_trend: "Long Trend", nse: "NSE Segment",
    };

    // Use the live bar's TSL (updated from tick price above) for display
    const liveTSL = TSL[len - 1];

    const dashboard: SwingTradeDashboard = {
      signal: dashSignal, trend: dashTrend,
      preset: presetLabels[presetKey] || "Standard",
      entry: ce > 0 ? ce : null,
      stopLoss: SL[uiBar] > 0 ? SL[uiBar] : null,
      trailingStop: liveTSL > 0 ? liveTSL : null,
      target1: TP1[uiBar] > 0 ? TP1[uiBar] : null,
      target2: TP2[uiBar] > 0 ? TP2[uiBar] : null,
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, slHit, tslHit,
      candleState: candleColorState[uiBar] || "neutral",
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors + hit markers
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — priceLines handle all level drawing.

    // MT4 candle colors: C'55,146,68' green, C'180,0,0' dark red, clrBlack neutral
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground !== "no") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") candleColors.push({ time: candles[i].time, color: "#379244" });
      else if (state === "short") candleColors.push({ time: candles[i].time, color: "#B40000" });
      else candleColors.push({ time: candles[i].time, color: "#5B9BD5" });
    }

    // Hit markers
    if (lastSignalBar >= 0 && (tp1Hit || tp2Hit || slHit)) {
      const sigDir = SIGNAL[lastSignalBar];
      const t1v = TP1[lastSignalBar], t2v = TP2[lastSignalBar], sv = SL[lastSignalBar];
      let _t1 = tp1Hit, _t2 = tp2Hit, _sl = slHit;
      for (let i = lastSignalBar + 1; i <= Math.min(lastClosedBar, len - 1); i++) {
        if (sigDir === DIR_LONG) {
          if (_t1 && candles[i].high >= t1v) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#379244", text: "✓ T1", size: 1 }); _t1 = false; }
          if (_t2 && candles[i].high >= t2v) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#379244", text: "✓ T2", size: 1 }); _t2 = false; }
          if (_sl && candles[i].low <= sv) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#B40000", text: "✗ SL", size: 1 }); _sl = false; }
        } else {
          if (_t1 && candles[i].low <= t1v) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#379244", text: "✓ T1", size: 1 }); _t1 = false; }
          if (_t2 && candles[i].low <= t2v) { markers.push({ time: candles[i].time, position: "belowBar", shape: "circle", color: "#379244", text: "✓ T2", size: 1 }); _t2 = false; }
          if (_sl && candles[i].high >= sv) { markers.push({ time: candles[i].time, position: "aboveBar", shape: "circle", color: "#B40000", text: "✗ SL", size: 1 }); _sl = false; }
        }
      }
    }

    // ── Price lines: axis badges + range lines from entry candle to right edge ──
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && ce > 0 && lastSignalBar >= 0) {
      const entryTime = candles[lastSignalBar].time;
      priceLines.push(
        { id: "sw-entry", price: ce,          color: "#1565C0", title: "Entry",     lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "sw-sl",    price: SL[uiBar],   color: "#C62828", title: "Stop Loss", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "sw-t1",   price: TP1[uiBar],  color: "#2E7D32", title: "Target_1",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
        { id: "sw-t2",   price: TP2[uiBar],  color: "#2E7D32", title: "Target_2",  lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime: entryTime },
      );
      if (showTrailingStop !== "no" && liveTSL > 0) {
        priceLines.push(
          { id: "sw-tsl", price: liveTSL, color: "#FF6F00", title: "T-SL", lineVisible: true, lineWidth: lw, lineStyle: 3, axisLabelVisible: true, fromTime: entryTime },
        );
      }
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
